import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import StandardModal from 'modals/StandardModal';
import Spinner from 'components/Spinner';
import { get } from 'data/services/apiService';
import { getApiUrl } from 'data/services/apiConstants';

import './HpdICardsModal.css';

/** Fetch enough cards for a typical building in one request. */
const CARDS_PER_PAGE = 100;

/**
 * Quick Links popup: scraped HPD Online I-card page images for the property.
 *
 * Uses the same main-image + thumbnail carousel pattern as ImagesWidget.
 */
const HpdICardsModal = () => {
	const dispatch = useDispatch();
	const carouselRef = useRef(null);
	const { propertyId, authToken } = useSelector((state) => ({
		propertyId: state.property.propertyData?.id,
		authToken: state.user.authToken,
	}));

	const [cards, setCards] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState(null);
	const [imageUrls, setImageUrls] = useState({});
	const [selectedPageId, setSelectedPageId] = useState(null);

	useEffect(() => {
		if (!propertyId) {
			return undefined;
		}

		let cancelled = false;
		setIsLoading(true);
		setError(null);

		get({
			url: `${getApiUrl()}/api/property/${propertyId}/hpd-historical-cards?per_page=${CARDS_PER_PAGE}`,
			authToken,
			dispatch,
		})
			.then((response) => {
				if (cancelled) {
					return;
				}
				setCards(Array.isArray(response?.data) ? response.data : []);
			})
			.catch((err) => {
				if (cancelled) {
					return;
				}
				setError(err?.message || 'Failed to load HPD I-Cards');
				setCards([]);
			})
			.finally(() => {
				if (!cancelled) {
					setIsLoading(false);
				}
			});

		return () => {
			cancelled = true;
		};
	}, [propertyId, authToken, dispatch]);

	const pages = useMemo(() => {
		const flattened = [];
		cards.forEach((card) => {
			(card.pages || []).forEach((page) => {
				flattened.push({
					...page,
					cardId: card.id,
					docDescription: card.doc_description,
					dateTaken: card.date_taken,
					docSubTypeName: card.doc_sub_type_name,
				});
			});
		});
		return flattened;
	}, [cards]);

	// Authenticated <img> loads: fetch each page as a blob, then object URL.
	useEffect(() => {
		let cancelled = false;
		const createdUrls = [];

		const loadImages = async () => {
			const next = {};
			for (const page of pages) {
				if (cancelled) {
					return;
				}
				try {
					const response = await fetch(
						`${getApiUrl()}/api/hpd-historical-card-pages/${page.id}/image`,
						{
							headers: authToken
								? {
										Authorization: authToken.startsWith('Bearer ')
											? authToken
											: `Bearer ${authToken}`,
								  }
								: {},
						}
					);
					if (!response.ok) {
						continue;
					}
					const blob = await response.blob();
					const objectUrl = URL.createObjectURL(blob);
					createdUrls.push(objectUrl);
					next[page.id] = objectUrl;
					// Select the first successfully loaded page.
					if (!cancelled) {
						setSelectedPageId((current) => current ?? page.id);
						setImageUrls((prev) => ({ ...prev, [page.id]: objectUrl }));
					}
				} catch (e) {
					// Skip broken pages; the carousel still shows the rest.
				}
			}
			if (!cancelled) {
				setImageUrls(next);
			}
		};

		setSelectedPageId(null);
		setImageUrls({});
		if (pages.length > 0) {
			loadImages();
		}

		return () => {
			cancelled = true;
			createdUrls.forEach((url) => URL.revokeObjectURL(url));
		};
	}, [pages, authToken]);

	const onCarouselBackward = useCallback(() => {
		if (carouselRef.current) {
			carouselRef.current.scrollBy({ left: -150, behavior: 'smooth' });
		}
	}, []);

	const onCarouselForward = useCallback(() => {
		if (carouselRef.current) {
			carouselRef.current.scrollBy({ left: 150, behavior: 'smooth' });
		}
	}, []);

	const selectedPage = pages.find((page) => page.id === selectedPageId);
	const selectedSrc = selectedPageId ? imageUrls[selectedPageId] : null;
	const selectedIndex = pages.findIndex((page) => page.id === selectedPageId);

	const selectRelative = (delta) => {
		if (pages.length === 0 || selectedIndex < 0) {
			return;
		}
		const nextIndex = (selectedIndex + delta + pages.length) % pages.length;
		const nextPage = pages[nextIndex];
		if (imageUrls[nextPage.id]) {
			setSelectedPageId(nextPage.id);
		}
	};

	return (
		<StandardModal title="I-Card (HPD)" size="xl" closeOnOutsideClick noFooter>
			<div className="hpd-icards-modal">
				{isLoading && (
					<div className="hpd-icards-modal__status">
						<Spinner />
					</div>
				)}
				{!isLoading && error && (
					<div className="hpd-icards-modal__status text-danger">{error}</div>
				)}
				{!isLoading && !error && pages.length === 0 && (
					<div className="hpd-icards-modal__status text-muted">
						No HPD I-Card images are available for this property yet.
					</div>
				)}
				{!isLoading && !error && pages.length > 0 && (
					<div className="hpd-icards-viewer">
						<div className="hpd-icards-main position-relative">
							{pages.length > 1 && (
								<>
									<button
										type="button"
										className="hpd-icards-nav hpd-icards-nav--left"
										onClick={() => selectRelative(-1)}
										aria-label="Previous image"
									>
										<i className="bi bi-arrow-left" />
									</button>
									<button
										type="button"
										className="hpd-icards-nav hpd-icards-nav--right"
										onClick={() => selectRelative(1)}
										aria-label="Next image"
									>
										<i className="bi bi-arrow-right" />
									</button>
								</>
							)}
							{selectedSrc ? (
								<img
									className="hpd-icards-main-image"
									src={selectedSrc}
									alt={
										selectedPage?.docDescription ||
										`HPD I-Card page ${selectedPage?.page_number}`
									}
								/>
							) : (
								<div className="hpd-icards-main-loading">
									<Spinner />
								</div>
							)}
						</div>

						{(selectedPage?.docDescription ||
							selectedPage?.docSubTypeName ||
							selectedPage?.dateTaken) && (
							<div className="hpd-icards-caption">
								<span>
									{selectedPage?.docDescription ||
										selectedPage?.docSubTypeName ||
										'I-Card'}
								</span>
								{selectedPage?.dateTaken && (
									<span className="hpd-icards-date">
										{selectedPage.dateTaken}
									</span>
								)}
								{pages.length > 0 && selectedIndex >= 0 && (
									<span className="hpd-icards-count">
										{selectedIndex + 1} / {pages.length}
									</span>
								)}
							</div>
						)}

						<div className="hpd-icards-carousel position-relative">
							<div
								className={`hpd-icards-carousel-arrows ${
									pages.length > 3 ? '' : 'd-none'
								}`}
							>
								<button
									type="button"
									className="btn btn-link hpd-icards-carousel-arrow hpd-icards-carousel-arrow--left"
									onClick={onCarouselBackward}
									aria-label="Scroll thumbnails left"
								>
									<i className="bi bi-arrow-left" />
								</button>
								<button
									type="button"
									className="btn btn-link hpd-icards-carousel-arrow hpd-icards-carousel-arrow--right"
									onClick={onCarouselForward}
									aria-label="Scroll thumbnails right"
								>
									<i className="bi bi-arrow-right" />
								</button>
							</div>

							<div className="hpd-icards-thumbs" ref={carouselRef}>
								{pages.map((page) => {
									const src = imageUrls[page.id];
									const isSelected = page.id === selectedPageId;
									return (
										<button
											key={page.id}
											type="button"
											className={`hpd-icards-thumb ${
												isSelected ? 'selected' : ''
											}`}
											onClick={() => src && setSelectedPageId(page.id)}
											disabled={!src}
										>
											{src ? (
												<img
													src={src}
													alt={
														page.docDescription ||
														`HPD I-Card page ${page.page_number}`
													}
												/>
											) : (
												<div className="hpd-icards-thumb-loading">
													<Spinner />
												</div>
											)}
										</button>
									);
								})}
							</div>
						</div>
					</div>
				)}
			</div>
		</StandardModal>
	);
};

export default HpdICardsModal;
